<template>
  <v-row>
    <v-col cols="12" md="4" lg="3" class="ml-3">
      <v-card>
        <v-card-text>
          <v-datetime-picker label="Start time"
                             v-model="filters.startTime"
                             :timePickerProps="{format: '24hr'}"
                             ref="dateTimePicker">
            <template #dateIcon>
              <v-icon>far fa-calendar-alt</v-icon>
            </template>
            <template #timeIcon>
              <v-icon>far fa-clock</v-icon>
            </template>
          </v-datetime-picker>
          <PaginatedAutocomplete
            v-model="filters.employee"
            label="Employee"
            item-text="phone"
            clearable
            item-value="id"
            :pagination="{perPage: 15, url:'scan-result-group/employee'}"/>
        </v-card-text>
        <v-card-actions>
          <VSpacer/>
          <v-btn icon @click="loadFirstPage()">
            <v-icon>far fa-sync</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
    <v-col cols="12" md="6" lg="6" offset-md="1">
      <v-timeline>
        <template v-for="(item, index) in timeline">
          <Walking :key="index"
                   v-if="timeline[index -1] && timeline[index -1].room && item.room && timeline[index -1].room.id !== item.room.id"/>
          <NewSession :key="`new-session-${index}`"
                      v-if="!timeline[index -1] || newSessionHasStarted(timeline[index -1].start_time, item.start_time)"/>
          <NoItemWhenExpected :key="index"
                              v-else-if="timeline[index -1] && noItemWhenExpected(timeline[index -1].start_time, item.start_time)"/>
          <Room :key="index" v-if="item.room" :item="item" @clickItem="openDialog(item)"/>
          <UnknownRoom :key="index" v-else :item="item" @clickItem="openDialog(item)"/>
        </template>
      </v-timeline>
      <div v-if="pagination.page === pagination.lastPage && timeline.length !== 0 && !pagination.isLoading"
           class="text-center">
        <v-btn class="ma-auto" color="primary" @click="loadFromLastItem">Reload</v-btn>
      </div>
      <v-overlay v-model="showDialog" v-if="!item">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-dialog v-model="showDialog" v-if="item" @click:outside="closeDialog" width="70%">
        <v-card height="80vh">
          <v-card-title>
            {{item.employee.phone}} visited {{item.room ? item.room.name : 'unknown room'}} in
            {{item.execution_time}}ms at {{item.start_time}}
          </v-card-title>
          <v-tabs vertical color="accent">
            <v-tab>
              Raw
            </v-tab>
            <v-tab>
              Truncated
            </v-tab>
            <v-tab>
              Grouped
            </v-tab>
            <v-tab>
              Average
            </v-tab>
            <v-tab>
              Invalid
            </v-tab>
            <v-tab>
              Valid
            </v-tab>
            <v-tab>
              Invalid groups
            </v-tab>
            <v-tab-item v-for="type in item.scan_results_by_type" :key="type.id">
              <v-data-table
                :headers="headers"
                :items="type"
                class="ma-5 elevation-0"
              ></v-data-table>
            </v-tab-item>
          </v-tabs>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import dayjs from 'dayjs';
import getTimelineItem from '../api/timeline/get.js';

import getPaginatedTimeline from '../api/timeline/getAll.js';

import PaginatedAutocomplete from '../components/PaginatedAutocomplete.vue';
import NewSession from '../components/timeline/item/NewSession.vue';
import NoItemWhenExpected from '../components/timeline/item/NoItemWhenExpected.vue';

import Room from '../components/timeline/item/Room.vue';
import UnknownRoom from '../components/timeline/item/UnknownRoom.vue';
import Walking from '../components/timeline/item/Walking.vue';

export default {
  name: 'Timeline',
  components: {Walking, NoItemWhenExpected, NewSession, UnknownRoom, Room, PaginatedAutocomplete},
  data() {
    return {
      filters: {
        startTime: dayjs().subtract(5, 'minute').toDate(),
        employee: null,
      },
      headers: [
        {text: 'Average rssi', value: 'average_rssi'},
        {text: 'Minimal rssi', value: 'minimal_rssi'},
        {text: 'Rssi', value: 'rssi'},
        {text: 'Grouping', value: 'grouping'},
        {text: 'Timestamp', value: 'timestamp'},
        {text: 'Room', value: 'room_name'},
      ],
      timeline: [],
      showDialog: false,
      item: null,
      pagination: {
        isLoading: false,
        page: 1,
      },
    };
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      async handler() {
        this.loadFirstPage();
      },
    },
  },
  mounted() {
    window.addEventListener('scroll', this.loadMorePostsWhenScrolledToBottom);
  },
  methods: {
    async loadFirstPage() {
      this.pagination.isLoading = true;
      this.pagination.page = 1;
      const {data, lastPage} = await getPaginatedTimeline({page: this.pagination.page, ...this.filters});
      this.pagination.lastPage = lastPage;
      this.timeline = data;
      this.pagination.isLoading = false;
    },
    async openDialog(item) {
      this.showDialog = true;
      this.item = await getTimelineItem(item.id);
    },
    closeDialog() {
      this.item = null;
      this.showDialog = false;
    },
    async loadMorePostsWhenScrolledToBottom() {
      if (window.innerHeight + window.scrollY > document.body.scrollHeight - 350) {
        if (this.pagination.isLoading) return;
        if (this.pagination.page === this.pagination.lastPage) return;

        this.pagination.isLoading = true;
        this.pagination.page++;

        const {data, lastPage} = await getPaginatedTimeline({page: this.pagination.page, ...this.filters});
        this.pagination.lastPage = lastPage;
        this.timeline.push(...data);
        this.pagination.isLoading = false;
      }
    },
    loadFromLastItem() {
      this.filters.startTime = this.timeline[this.timeline.length - 1].start_time;
      this.loadFirstPage();
    },
    newSessionHasStarted(previousDate, currentDate) {
      return dayjs(currentDate).isAfter(dayjs(previousDate).add(1, 'minute'));
    },
    noItemWhenExpected(previousDate, currentDate) {
      return dayjs(currentDate).isAfter(dayjs(previousDate).add(29, 'seconds'));
    },
  },
};
</script>

<style>
.v-card--link:focus:before {
  opacity: 0 !important;
}
</style>
